/**
 * iter-371 — OTP Gate + View-Limit config dialog for the Edit BOQ page.
 *
 * Owner/Admin toggles whether the Public BOQ link requires an email OTP
 * before the contents are shown, and sets an optional cap on the total
 * anonymous views (Admin views are excluded from the counter).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ShieldCheck, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OTPConfigDialog({ open, onOpenChange, worksheet, onSaved }) {
  const [enabled, setEnabled] = useState(false);
  const [viewLimit, setViewLimit] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && worksheet) {
      setEnabled(!!worksheet.otp_gate_enabled);
      setViewLimit(worksheet.otp_view_limit != null ? String(worksheet.otp_view_limit) : '');
    }
  }, [open, worksheet]);

  const handleSave = async () => {
    if (!worksheet?.id) return;
    setSaving(true);
    try {
      const parsed = viewLimit === '' ? null : Math.max(1, Math.min(1000, parseInt(viewLimit, 10) || 0));
      await axios.patch(`${API}/boq-worksheets/${worksheet.id}/otp-config`, {
        otp_gate_enabled: enabled,
        otp_view_limit: parsed,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Access settings saved');
      onOpenChange(false);
      onSaved?.({ otp_gate_enabled: enabled, otp_view_limit: parsed });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="otp-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-700" />
            Public BOQ Access Security
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-stone-50 border border-stone-200 rounded-md p-3 flex items-start gap-3">
            <Lock size={18} className={enabled ? 'text-emerald-600 mt-0.5' : 'text-stone-400 mt-0.5'} />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="otp-gate-switch" className="font-medium text-sm text-stone-900">Require email OTP to view</Label>
                <Switch
                  id="otp-gate-switch"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  data-testid="otp-gate-switch"
                />
              </div>
              <p className="text-xs text-stone-500 mt-1">
                Client / vendor will be asked to enter their email, receive a 6-digit code,
                and unlock the quotation for 30 days on their device.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Anonymous view limit (optional)</Label>
            <Input
              type="number"
              min={1}
              max={1000}
              placeholder="e.g. 5 (leave blank for unlimited)"
              value={viewLimit}
              onChange={(e) => setViewLimit(e.target.value)}
              className="mt-1"
              data-testid="otp-view-limit-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              After this many anonymous views the link is blocked. Admin/owner/staff
              logged-in views are <strong>not</strong> counted.
            </p>
            {worksheet?.otp_view_count != null && (
              <p className="text-xs text-amber-700 mt-1" data-testid="otp-current-view-count">
                Current count: <strong>{worksheet.otp_view_count}</strong> anonymous views
              </p>
            )}
          </div>

          {enabled && worksheet?.public_token && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-800 space-y-2" data-testid="otp-admin-preview-block">
              <div className="font-medium">Testing your gated link?</div>
              <div>Because you&apos;re logged in, opening the plain public link in this browser now hits the OTP wall (which is what a real client sees).</div>
              <a
                href={`/public/boq/${worksheet.public_token}?admin_preview=1`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium"
                data-testid="otp-admin-preview-btn"
              >
                Preview as Admin (skip OTP) →
              </a>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="otp-config-save-btn" aria-busy={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
